import { ChevronRightIcon, MagnifyingGlassIcon, StarIcon } from '@heroicons/react/20/solid'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { MultiSelectDropdown } from '@/components/forms/MultiSelectDropdown'
import { AllDealersHeader } from '@/components/layout/DealerLayout'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { StateContainer } from '@/components/ui/StateContainer'
import { SLA_LATE } from '@/pages/RoStatusDashboard/buckets'
import {
  formatDuration,
  GUIDELINE_HOURS,
  hoursSince,
  managedTypeFilter,
  QUEUE_GROUPS,
  QUEUES,
  SLA_HOURS,
} from '@/pages/WorkQueue/queue'
import QueueKpiRow, { WorkSummary } from '@/pages/WorkQueue/QueueKpiRow'
import {
  Flag,
  flagFor,
  GUIDELINE,
  shortLabel,
  Swatch,
  swatchFor,
} from '@/pages/WorkQueue/QueueTile'
import { downloadWorkloadExport, getWorkQueueByDealer } from '@/services/repairOrders.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import ExportButton from './ExportButton'
import {
  availableLevels,
  buildTree,
  filterRows,
  levelById,
  queueLink,
  sortTree,
  STAFF,
  staffForNode,
  staffOptions,
  startingDealer,
  sumRows,
  UNASSIGNED,
} from './workload'

// Ready and Closed are RO Intake work: a dealer without RO Intake has none to count.
const INTAKE_QUEUES = ['ready', 'closed']

// Time in queue moves while the page is open.
const useNow = (intervalMs) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])
  return now
}

/**
 * AllDealersWorkload
 *
 * WARP-960 / WARP-963 — the Dashboard with "All dealers" picked: the Work Queue's counts
 * for every dealer the user can reach, broken down by dealer, admin, assistant manager,
 * or manager. Every count opens that Work Queue tab, narrowed to its row, so there is one
 * place to do the work and this page only says where it is.
 *
 * One screen for every level (Michelle, 2026-10-01: the same screen with a "select
 * assistant manager" filter, not a page per level). It opens by dealer for everyone; the
 * break-down control groups by person. The level and the staff filters live in the
 * address, so a link shares the view.
 *
 * The people are the HubSpot names on each dealer; see `workload.js` for why the page,
 * not the API, groups them.
 */
const AllDealersWorkload = () => {
  const { isWpcUser, isWpcExecutive } = useContext(UserStoreContext)
  const { type } = useContext(DashboardStoreContext)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const now = useNow(60 * 1000)

  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [exporting, setExporting] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())
  const [sort, setSort] = useState({ key: 'oldest', direction: 'desc' })

  // SLA signals (late, oldest waiting) are WPC's alone, as on the dealer dashboard. The
  // staff are WPC's people, so only WPC breaks down by them.
  const showWpcSignals = isWpcUser

  useEffect(() => {
    const controller = new AbortController()
    setRows(null)
    getWorkQueueByDealer(
      managedTypeFilter({ dealerId: null, isWpcExecutive, type }),
      setError,
      controller.signal,
    ).then((response) => {
      if (!response || controller.signal.aborted) return
      const dealers = response.dealers || []
      // Only an RO Intake dealer has a dashboard (2026-09-24). With none, the Dealers list
      // is where to go next; with one dealer, there is nothing to choose between.
      if (!dealers.some((row) => row.isRoIntakeEnabled)) {
        navigate('/dashboard', { replace: true })
        return
      }
      if (dealers.length === 1) {
        navigate(`/ro-intake/${dealers[0].id}`, { replace: true })
        return
      }
      setError(null)
      setRows(dealers)
    })

    return () => controller.abort()
  }, [isWpcExecutive, type])

  // The page's filters, from the address: the staff names, then the level.
  const staffFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(STAFF).map(([field, { param }]) => [field, searchParams.getAll(param)]),
      ),
    [searchParams],
  )
  const scopeRows = useMemo(() => rows || [], [rows])
  const filteredRows = useMemo(
    () => filterRows(scopeRows, { ...staffFilters, query }),
    [scopeRows, staffFilters, query],
  )

  const levels = useMemo(
    () => (showWpcSignals ? availableLevels(scopeRows) : [levelById('dealer')]),
    [scopeRows, showWpcSignals],
  )
  const requestedLevel = levels.find((level) => level.id === searchParams.get('by'))
  const level = requestedLevel || levelById('dealer')

  const tree = useMemo(
    () => sortTree(buildTree(filteredRows, level.id, scopeRows), sort),
    [filteredRows, level.id, scopeRows, sort],
  )
  const totals = useMemo(() => sumRows(filteredRows), [filteredRows])
  const start = showWpcSignals ? startingDealer(filteredRows) : null

  // Sets a param to one value or several (repeated), or clears it.
  const setParam = (name, value) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(name)
        ;[].concat(value || []).forEach((item) => next.append(name, item))
        return next
      },
      { replace: true },
    )

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const sortBy = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' ? 'asc' : 'desc' },
    )

  const pageStaff = staffForNode({ type: 'total' }, staffFilters)
  const ageOf = (iso) => (iso ? hoursSince(iso, now) : null)
  const intakeHidden = (node, queue) =>
    node.type === 'dealer' && !node.row.isRoIntakeEnabled && INTAKE_QUEUES.includes(queue.id)

  // A count's cell: a link into that Work Queue tab, shaded by how much is there.
  const maxByQueue = useMemo(
    () =>
      Object.fromEntries(
        QUEUES.map((queue) => [
          queue.id,
          Math.max(1, ...filteredRows.map((row) => row.queues?.[queue.countsKey]?.total || 0)),
        ]),
      ),
    [filteredRows],
  )

  const renderCount = (node, queue) => {
    if (intakeHidden(node, queue)) {
      return (
        <span className="text-charcoal-300" title="RO Intake is off for this dealer">
          —
        </span>
      )
    }
    const counts = node.counts.queues[queue.countsKey] || {}
    const value = counts.total || 0
    if (!value) return <span className="text-charcoal-300 tabular-nums">0</span>

    const flag = showWpcSignals ? flagFor(queue, counts) : null
    // Shade against the busiest dealer; a group by its average dealer.
    const share = Math.min(1, value / node.rows.length / maxByQueue[queue.id])
    // An inactive dealer has no pages of its own to open.
    const isInactive = node.type === 'dealer' && !node.row.isActive
    const to = isInactive
      ? null
      : queueLink(queue, {
          dealerId: node.type === 'dealer' ? node.row.id : null,
          staff: staffForNode(node, staffFilters),
        })
    const content = (
      <>
        <span>{value.toLocaleString()}</span>
        {flag && <Flag compact flag={flag} />}
      </>
    )
    const className =
      'inline-flex min-w-[3.25rem] items-center justify-end gap-1.5 rounded-md px-2 py-1 tabular-nums'

    if (!to) {
      return (
        <span
          className={className}
          title={
            isInactive
              ? 'This dealer is inactive, so it has no Work Queue of its own.'
              : 'No one is named in HubSpot. Open a dealer below.'
          }
        >
          {content}
        </span>
      )
    }
    return (
      <Link
        aria-label={`${value} ${shortLabel(queue)}${
          flag ? `, ${flag.value} ${flag.label}` : ''
        }, ${node.name}`}
        className={mergeClassNames(
          className,
          'text-charcoal-900 hover:ring-1 hover:ring-blue-800',
        )}
        style={{ backgroundColor: `rgba(4, 84, 133, ${(0.05 + share * 0.22).toFixed(2)})` }}
        to={to}
      >
        {content}
      </Link>
    )
  }

  const renderRows = (nodes) =>
    nodes.flatMap((node) => {
      const isOpen = expanded.has(node.key)
      const oldest = ageOf(node.counts.oldestReceivedAt)
      const indent = { paddingLeft: `${1 + node.depth * 1.5}rem` }

      const name =
        node.type === 'group' ? (
          <div className="flex min-w-[16rem] items-center gap-2" style={indent}>
            <button
              aria-expanded={isOpen}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.name}`}
              className="text-charcoal-400 hover:text-charcoal-900 -ml-1 rounded p-0.5"
              onClick={() => toggle(node.key)}
              type="button"
            >
              <ChevronRightIcon
                className={mergeClassNames('h-4 w-4 transition-transform', isOpen && 'rotate-90')}
              />
            </button>
            <span className="text-charcoal-500 border-charcoal-200 rounded border px-1 text-[9px] font-bold uppercase tracking-widest">
              {STAFF[node.field].label}
            </span>
            <span
              className={mergeClassNames(
                'font-semibold',
                node.name === UNASSIGNED && 'text-charcoal-500 italic',
              )}
            >
              {node.name}
            </span>
            {node.isSelf && (
              <span
                className="rounded-full bg-blue-50 px-2 py-px text-[10px] font-semibold text-blue-800"
                title={`Also the ${STAFF[
                  node.trail[node.trail.length - 1].field
                ].label.toLowerCase()} above`}
              >
                self
              </span>
            )}
            {node.parents > 1 && (
              <span
                className="rounded-full px-2 py-px text-[10px] font-semibold"
                style={{ backgroundColor: GUIDELINE.fill, color: GUIDELINE.ink }}
                title={`These dealers report through ${node.parents} different ${STAFF[
                  node.field === 'admin' ? 'assistantManager' : 'manager'
                ].label.toLowerCase()}s in HubSpot. Each dealer still counts once.`}
              >
                under {node.parents}
              </span>
            )}
          </div>
        ) : (
          <div className="flex min-w-[16rem] items-center gap-2" style={indent}>
            {start && start.id === node.row.id && (
              <StarIcon
                aria-label="Oldest waiting RO"
                className="text-gold-dark h-4 w-4 shrink-0"
              />
            )}
            {node.row.isActive ? (
              <Link
                className="text-charcoal-900 hover:text-blue-800 hover:underline"
                // Its dashboard, or for a dealer without RO Intake, its Outstanding List.
                to={
                  node.row.isRoIntakeEnabled
                    ? `/ro-intake/${node.row.id}`
                    : `/dealer/${node.row.id}/detail`
                }
              >
                {node.name}
              </Link>
            ) : (
              <span className="text-charcoal-600">{node.name}</span>
            )}
            {!node.row.isActive && (
              <span
                className="rounded px-1 text-[10px] font-semibold"
                style={{ backgroundColor: SLA_LATE.fill, color: SLA_LATE.ink }}
                title="This dealer is inactive, but still has open repair orders. They count in the Work Queue."
              >
                Inactive
              </span>
            )}
            {node.row.isActive && node.row.isRoIntakeEnabled && (
              <span
                className="rounded bg-blue-50 px-1.5 py-px text-[10px] font-semibold text-blue-800"
                title="This dealer logs ROs through RO Intake. Without it, only Outstanding List work shows."
              >
                RO Intake
              </span>
            )}
          </div>
        )

      const row = (
        <tr className="border-charcoal-100 hover:bg-charcoal-50 border-t" key={node.key}>
          <td className="py-1.5 pr-3 text-sm">{name}</td>
          <td className="text-charcoal-500 px-3 py-1.5 text-right text-sm tabular-nums">
            {level.id === 'dealer' ? (
              <span className="block text-left">{node.row.admin || '—'}</span>
            ) : (
              node.rows.length
            )}
          </td>
          {QUEUES.map((queue, index) => (
            <td
              className={mergeClassNames(
                'px-1 py-1 text-right text-sm',
                index > 0 &&
                  QUEUES[index - 1].group !== queue.group &&
                  'border-charcoal-100 border-l',
              )}
              key={queue.id}
            >
              {renderCount(node, queue)}
            </td>
          ))}
          {showWpcSignals && (
            <td
              className={mergeClassNames(
                'border-charcoal-100 border-l px-4 py-1.5 text-right text-sm tabular-nums',
                oldest > SLA_HOURS ? 'font-semibold' : 'text-charcoal-600',
              )}
              style={oldest > SLA_HOURS ? { color: SLA_LATE.ink } : undefined}
            >
              {oldest === null ? '—' : formatDuration(oldest)}
            </td>
          )}
        </tr>
      )
      return node.type === 'group' && isOpen ? [row, ...renderRows(node.children)] : [row]
    })

  const headerCell = (key, label, { align = 'right', className } = {}) => {
    const active = sort.key === key
    let ariaSort = 'none'
    if (active) ariaSort = sort.direction === 'asc' ? 'ascending' : 'descending'
    return (
      <th
        aria-sort={ariaSort}
        className={mergeClassNames('px-3 pb-2 pt-1 align-bottom', `text-${align}`, className)}
        key={key}
      >
        <button
          className={mergeClassNames(
            'inline-flex items-center text-[10px] font-bold uppercase tracking-widest',
            active ? 'text-blue-800' : 'text-charcoal-700 hover:text-charcoal-900',
          )}
          onClick={() => sortBy(key)}
          type="button"
        >
          {label}
          {active && (
            <span aria-hidden className="ml-1">
              {sort.direction === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </button>
      </th>
    )
  }

  // The staff filters, as the Dealers list has them: search, several names at once.
  const staffSelect = (field) => {
    const { param, placeholder } = STAFF[field]
    const names = staffOptions(scopeRows, field, staffFilters)
    const chosen = staffFilters[field]
    // Hidden when there is nobody to choose between, unless a link already picked one.
    if (names.length < 2 && chosen.length === 0) return null
    return (
      <MultiSelectDropdown
        className="w-full md:max-w-[220px]"
        containerClassName="w-full md:w-auto"
        id={`workload-${param}-filter`}
        key={field}
        onChange={(options) => {
          setExpanded(new Set())
          setParam(
            param,
            options.map((option) => option.id),
          )
        }}
        options={names.map((name) => ({ id: name, label: name }))}
        placeholder={placeholder}
        search
        value={chosen.map((name) => ({ id: name, label: name }))}
      />
    )
  }

  const loading = rows === null && !error

  return (
    <>
      <Helmet>
        <title>Dashboard · All dealers</title>
      </Helmet>

      <AllDealersHeader>{rows && `${rows.length.toLocaleString()} dealers`}</AllDealersHeader>

      <StateContainer error={error}>
        <div className="flex w-full flex-col space-y-4">
          {/* The same header as one dealer's dashboard, less Log ROs (2026-10-12). */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <WorkSummary counts={totals} loading={loading} showLate={showWpcSignals} />
            {/* The table below as a spreadsheet: the rows on screen, filters and search
                included, with their counts and dollars. */}
            <ExportButton
              disabled={loading || filteredRows.length === 0}
              exporting={exporting}
              label="Export the dealer workload"
              onClick={() =>
                downloadWorkloadExport(
                  filteredRows.map((row) => row.id),
                  setExporting,
                  (message) => toast(message, 'error'),
                )
              }
            />
          </div>

          {/* The queue totals, in the Work Queue's two groups, then WPC's Urgency cards. */}
          <QueueKpiRow
            counts={totals}
            linkFor={(queue, filter) => queueLink(queue, { filter, staff: pageStaff })}
            loading={loading}
            showWpcSignals={showWpcSignals}
          />

          {start && (
            <div className="bg-gold-light flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-4 py-3 text-sm">
              <StarIcon aria-hidden className="text-gold-dark h-4 w-4" />
              <span className="text-charcoal-900">
                Start with <span className="font-semibold">{start.name}</span>. It holds the oldest
                waiting RO:{' '}
                <span className="font-semibold tabular-nums">
                  {formatDuration(ageOf(start.oldestReceivedAt))}
                </span>{' '}
                since received.
              </span>
              <Link
                className="ml-auto font-semibold text-blue-800 hover:underline"
                to={`/work-queue/${start.id}`}
              >
                Open its Work Queue →
              </Link>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            {levels.length > 1 && (
              <div className="flex flex-col gap-1">
                <span className="text-charcoal-500 px-1 text-[10px] font-bold uppercase tracking-widest">
                  Break down by
                </span>
                <SegmentedTabs
                  activeTab={level.id}
                  ariaLabel="Break down by"
                  onChange={(id) => {
                    setExpanded(new Set())
                    setParam('by', id)
                  }}
                  panelId="workload-table"
                  tabs={levels.map(({ id, label }) => ({ id, label }))}
                />
              </div>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {showWpcSignals && ['manager', 'assistantManager', 'admin'].map(staffSelect)}
              <label className="relative block">
                <span className="sr-only">Search dealers</span>
                <MagnifyingGlassIcon
                  aria-hidden
                  className="text-charcoal-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                />
                <input
                  className="border-brownGray h-[38px] w-56 rounded-md border bg-white pl-9 pr-3 text-sm shadow-sm"
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search dealers"
                  type="search"
                  value={query}
                />
              </label>
            </div>
          </div>

          <div
            className="relative overflow-x-auto rounded-2xl bg-white shadow-sm"
            id="workload-table"
            role="tabpanel"
          >
            <table className="w-full min-w-[1000px] border-collapse">
              <thead>
                <tr>
                  <th aria-hidden colSpan={2} />
                  {QUEUE_GROUPS.map((group, index) => (
                    <th
                      className={mergeClassNames(
                        'text-charcoal-500 px-3 pt-3 text-left text-[10px] font-bold uppercase tracking-widest',
                        index > 0 && 'border-charcoal-100 border-l',
                      )}
                      colSpan={QUEUES.filter((queue) => queue.group === group.id).length}
                      key={group.id}
                    >
                      {group.label}
                    </th>
                  ))}
                  {showWpcSignals && <th aria-hidden className="border-charcoal-100 border-l" />}
                </tr>
                <tr>
                  {headerCell(
                    'name',
                    level.path.length
                      ? [...level.path.map((field) => STAFF[field].label), 'Dealer'].join(' › ')
                      : 'Dealer',
                    { align: 'left', className: 'pl-4' },
                  )}
                  {level.id === 'dealer'
                    ? headerCell('admin', 'Admin', { align: 'left' })
                    : headerCell('dealers', 'Dealers')}
                  {QUEUES.map((queue, index) =>
                    headerCell(
                      queue.countsKey,
                      <>
                        <Swatch color={swatchFor(queue)} />
                        {shortLabel(queue)}
                      </>,
                      {
                        className:
                          index > 0 && QUEUES[index - 1].group !== queue.group
                            ? 'border-charcoal-100 border-l'
                            : undefined,
                      },
                    ),
                  )}
                  {showWpcSignals &&
                    headerCell('oldest', 'Oldest waiting', {
                      className: 'border-charcoal-100 border-l pr-4',
                    })}
                </tr>
              </thead>
              <tbody>
                {!loading && (
                  <tr className="bg-charcoal-50 border-charcoal-100 border-t font-semibold">
                    <td className="py-2 pl-4 pr-3 text-sm">
                      Total
                      {pageStaff.length > 0 && (
                        <span className="text-charcoal-500 ml-2 font-normal">
                          {pageStaff.map(({ name }) => name).join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-sm tabular-nums">
                      {level.id === 'dealer' ? '' : filteredRows.length.toLocaleString()}
                    </td>
                    {QUEUES.map((queue, index) => (
                      <td
                        className={mergeClassNames(
                          'px-1 py-1 text-right text-sm',
                          index > 0 &&
                            QUEUES[index - 1].group !== queue.group &&
                            'border-charcoal-100 border-l',
                        )}
                        key={queue.id}
                      >
                        {renderCount(
                          {
                            type: 'total',
                            key: 'total',
                            name: 'Total',
                            rows: filteredRows,
                            counts: totals,
                          },
                          queue,
                        )}
                      </td>
                    ))}
                    {showWpcSignals && (
                      <td className="border-charcoal-100 border-l px-4 py-2 text-right text-sm tabular-nums">
                        {totals.oldestReceivedAt
                          ? formatDuration(ageOf(totals.oldestReceivedAt))
                          : '—'}
                      </td>
                    )}
                  </tr>
                )}
                {renderRows(tree)}
                {loading && (
                  <tr>
                    <td
                      className="text-charcoal-400 px-4 py-10 text-center text-sm"
                      colSpan={QUEUES.length + 3}
                    >
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && tree.length === 0 && (
                  <tr>
                    <td
                      className="text-charcoal-400 px-4 py-10 text-center text-sm"
                      colSpan={QUEUES.length + 3}
                    >
                      No dealers match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {showWpcSignals && !loading && (
            <div className="text-charcoal-500 flex flex-wrap gap-x-5 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <Flag compact flag={{ value: 3, label: 'late', tone: SLA_LATE }} />
                Past the {SLA_HOURS}-hour SLA, or aged out
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Flag compact flag={{ value: 3, label: 'waiting', tone: GUIDELINE }} />
                Waiting over {GUIDELINE_HOURS} hours since the last update
              </span>
              <span className="inline-flex items-center gap-1.5">
                <StarIcon aria-hidden className="text-gold-dark h-3.5 w-3.5" />
                The dealer with the oldest Ready or Closed RO
              </span>
            </div>
          )}
        </div>
      </StateContainer>
    </>
  )
}

export default AllDealersWorkload
